import { useState } from 'react';
import { useCampaignApi } from '../../services/apiService';
import { toast } from 'sonner';
import { type Campaign } from './types';

export function useCampaignBrief() {
    const { generateCampaignBrief } = useCampaignApi();
    
    const [isBriefDialogOpen, setIsBriefDialogOpen] = useState(false);
    const [briefCampaign, setBriefCampaign] = useState<Campaign | null>(null);
    const [briefContent, setBriefContent] = useState('');
    const [briefPdfUrl, setBriefPdfUrl] = useState<string | null>(null);
    const [briefError, setBriefError] = useState<string | null>(null);
    const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
    const [briefCopied, setBriefCopied] = useState(false);

    const handleCloseBriefDialog = () => {
        setIsBriefDialogOpen(false);
        setBriefCampaign(null);
        setBriefContent('');
        setBriefPdfUrl(null);
        setBriefError(null);
        setIsGeneratingBrief(false);
        setBriefCopied(false);
    };

    const handleGenerateBrief = async (campaign: Campaign) => {
        setBriefCampaign(campaign);
        setBriefContent('');
        setBriefPdfUrl(null);
        setBriefError(null);
        setIsBriefDialogOpen(true);
        setIsGeneratingBrief(true);
        setBriefCopied(false);

        try {
            const response = await generateCampaignBrief(Number(campaign.id));
            if (response.success && response.brief) {
                setBriefContent(response.brief);
                setBriefPdfUrl(response.pdfUrl ?? null);
            } else {
                const message = response.errorMessage || 'Unable to generate campaign brief. Please try again later.';
                setBriefError(message);
                toast.error(message);
            }
        } catch (error) {
            console.error('Failed to generate campaign brief', error);
            const fallback = 'An unexpected error occurred while generating the campaign brief. Please try again later.';
            setBriefError(fallback);
            toast.error(fallback);
        } finally {
            setIsGeneratingBrief(false);
        }
    };

    const handleCopyBrief = async () => {
        if (!briefContent) {
            return;
        }

        try {
            await navigator.clipboard.writeText(briefContent);
            setBriefCopied(true);
            toast.success('Campaign brief copied to clipboard');
            setTimeout(() => setBriefCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy campaign brief', error);
            toast.error('Failed to copy campaign brief');
        }
    };

    return {
        isBriefDialogOpen,
        briefCampaign,
        briefContent,
        briefPdfUrl,
        briefError,
        isGeneratingBrief,
        briefCopied,
        handleCloseBriefDialog,
        handleGenerateBrief,
        handleCopyBrief
    };
}
